/**
 * Public surface of the printed slate: conditions in, a PDF in the diver's
 * hands.
 *
 * `pdf.ts`, and the jsPDF behind it, is loaded on demand rather than at
 * startup. Nothing in the builder needs it until someone actually asks for a
 * page, and first paint over a hotel wifi is the thing a diver waits on. The
 * service worker precaches the emitted chunk like every other file, so the
 * export still works with no signal — `loadRenderer` only fails in the case
 * where the chunk was never cached in the first place.
 *
 * **Nothing here may statically import or re-export from `./pdf`.** A single
 * `export { x } from "./pdf"` pulls jsPDF back into the eager graph and
 * silently undoes all of this, with no test to catch it. That is why
 * `slateFilename` lives here rather than next to the renderer that uses it.
 */

import type { CompiledSlot } from "../shelf";
import { layoutSlate, type SlateLayout, type SlateOptions } from "./layout";

export {
  DEFAULT_SLATE_OPTIONS,
  GOPRO_GRID,
  layoutSlate,
  measureQrMm,
  MIN_QR_MM,
  SLOTS_PER_PAGE,
} from "./layout";
export { PAGE_SIZES, MM, type PageSizeKey, type DrawOp, type Page } from "./ops";
export type { GridSpec, SlateLayout, SlateOptions } from "./layout";

/** Filename that sorts sensibly in a downloads folder and survives AirDrop. */
export function slateFilename(heading: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const slug =
    heading
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 40) || "dive-slate";
  return `${slug}-${stamp}.pdf`;
}

async function loadRenderer(): Promise<typeof import("./pdf")> {
  try {
    return await import("./pdf");
  } catch {
    // Precaching tolerates partial failure by design, and the worker cannot
    // install at all before the first visit finishes. Either way the honest
    // thing to say is what to do about it, not "you need a connection" —
    // which is untrue once the chunk is cached, and would teach a diver to
    // distrust the offline badge.
    throw new Error(
      "Couldn’t load the PDF exporter. Connect once and it will work offline from then on.",
    );
  }
}

/** Build the file and hand it to the browser. */
export async function downloadSlate(
  slots: readonly CompiledSlot[],
  options: SlateOptions,
): Promise<SlateLayout> {
  const { renderPdf } = await loadRenderer();
  const layout = layoutSlate(slots, options);
  renderPdf(layout.pages).save(slateFilename(options.heading));
  return layout;
}

/**
 * The same document as a blob — for an in-app preview, or for Web Share on a
 * phone, where "download" is a less useful verb than "send it to the boat's
 * group chat".
 */
export async function slateBlob(
  slots: readonly CompiledSlot[],
  options: SlateOptions,
): Promise<{ blob: Blob; layout: SlateLayout; filename: string }> {
  const { renderPdf } = await loadRenderer();
  const layout = layoutSlate(slots, options);
  const blob = renderPdf(layout.pages).output("blob");
  return { blob, layout, filename: slateFilename(options.heading) };
}
